import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { RenderForm } from '../../../../../components/init-form/render-form';
import { PrintIcon } from '../../../../../components/init-form/print-icon';
import { OnBehalfProvider } from '../../../../../container/onBehalfContext';
import { FORM_ROLES } from '../../../../../constants/roles';
import { getInitFromById, getOnBehalfFormPDF, submitForm } from '../../../../../services/form';
import { isResponseOk } from '../../../../../utility/isResponseOk';
import { buildSubmitterFormFields } from '../../../../../utility/forms/submitter-form-fields';
import './styles.scss';

// Why the backend refused, in its words. On-behalf refusals come as a plain
// `detail` (the send-form page's `form` key would make the API client leave
// for its full-page "form submitted" screen, which is no place for a modal).
const refusalOf = (e, fallback) => {
  const detail = e?.response?.data?.detail;

  return typeof detail === 'string' && detail ? detail : fallback;
};

/**
 * A "Sent" row of the Submissions folder, opened to be submitted on behalf of
 * its submitter (the 'Ability to view and submit forms with status "Sent"' permission).
 *
 * The backend serves and records the form as that submitter -- `row.id` is the
 * pending notification -- so the modal only has to render the form the way the
 * send-form page would, and say whom it is for. No draft: a draft would turn the
 * row into the submitter's own "In Progress" one, which cannot be reopened here.
 *
 * @param {object} row - The folder row: id, form_initiation_id, on_behalf_submission_id, name.
 * @param {() => void} closeModal
 * @param {() => void} onSubmitted - Called once the submission went through.
 */
function SubmitOnBehalfModal({ row, closeModal, onSubmitted }) {
  const [formData, setFormData] = useState(null);
  const [initData, setInitData] = useState({});
  const [errors, setErrors] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isPDFGenerating, setIsPDFGenerating] = useState(false);

  const initiationId = row?.form_initiation_id;
  const notificationId = row?.id;
  // Set on strict-order forms with "Specify submissions number = Multiple".
  const submissionId = row?.on_behalf_submission_id || null;

  const onBehalfOf = formData?.on_behalf_of ?? null;
  const onBehalfOfName = onBehalfOf ? [onBehalfOf.last_name, onBehalfOf.first_name].filter(Boolean).join(' ') : row?.submitter;

  useEffect(() => {
    if (!initiationId || !notificationId) {
      return;
    }

    getInitFromById(initiationId, undefined, submissionId, notificationId)
      .then((res) => {
        setFormData(res);

        const form_fields = buildSubmitterFormFields(res, FORM_ROLES.FORM_SUBMITTER);

        if (form_fields) {
          setInitData({ form_fields });
        }
      })
      .catch((e) => {
        console.log(e);
        toast.error(refusalOf(e, 'The form could not be opened'), { hideProgressBar: false });
        closeModal();
      });
    // eslint-disable-next-line
  }, [initiationId, notificationId, submissionId]);

  const handelInitSettings = useCallback((res, key) => {
    setInitData((prevState) => ({ ...prevState, [key]: res }));
  }, []);

  const onClearForm = () => {
    const form_fields = buildSubmitterFormFields(formData, FORM_ROLES.FORM_SUBMITTER);

    if (form_fields) {
      setInitData({ form_fields });
    }
  };

  // Prints the form as it stands in the modal -- as sent, plus whatever has been
  // filled in so far -- the way the finished submission would print. Nothing is saved.
  const onPrint = async () => {
    setIsPDFGenerating(true);

    try {
      const response = await getOnBehalfFormPDF(initiationId, notificationId, {
        data: initData.form_fields,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      if (isResponseOk(response)) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        window.open(window.URL.createObjectURL(blob), '_blank');
      }
    } catch (e) {
      console.log(e);
      toast.error('The form could not be printed', { hideProgressBar: false });
    } finally {
      setIsPDFGenerating(false);
    }
  };

  const onSend = () => {
    setIsDisabled(true);

    submitForm(initiationId, { data: initData.form_fields, is_saved: false }, undefined, submissionId, notificationId)
      .then(() => {
        setIsDisabled(false);
        toast.success(`The form was submitted on behalf of ${onBehalfOfName}`, { hideProgressBar: false });
        onSubmitted();
      })
      .catch((e) => {
        setIsDisabled(false);
        console.log('Submit form error: ', e);

        const nonFieldErrors = e?.response?.data?.non_field_errors;

        if (Array.isArray(nonFieldErrors)) {
          setErrors(nonFieldErrors);
          toast.error('Please fill out the required fields', { hideProgressBar: false });

          const firstErrorObjId = Object.keys(nonFieldErrors[0] ?? {})[0];
          const formElem = firstErrorObjId ? document.getElementById(`element-${firstErrorObjId}`) : null;

          if (formElem) {
            formElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else {
          toast.error(refusalOf(e, 'The form could not be submitted'), { hideProgressBar: false });
        }
      });
  };

  return (
    <Modal
      // `init-page` is the class the send-form page's <main> carries: every rule that
      // makes a rendered form fillable (inputs, checkboxes, pickers, signatures are
      // `pointer-events: none` by default) is scoped to it, so the modal has to carry
      // it too to behave like that page.
      className="modal-default submit-on-behalf-modal init-page general-form-tab-content"
      isOpen
      onRequestClose={closeModal}
      // A half-filled form must not vanish on a stray click outside it (Esc still closes).
      shouldCloseOnOverlayClick={false}
      contentLabel="Submit the form on behalf of the submitter"
      ariaHideApp={false}
    >
      <h2 className="modal-header">
        <div className="submit-on-behalf-modal__title" data-testid="on-behalf-banner">
          <span>
            Submitting on behalf of: <strong>{onBehalfOfName}</strong>
            {onBehalfOf?.position ? `, ${onBehalfOf.position}` : ''}
          </span>
          {row?.name && <span className="submit-on-behalf-modal__form-name">{row.name}</span>}
        </div>
        <div className="submit-on-behalf-modal__header-actions">
          <button
            className="btn btn-outline btn-outline-secondary d-flex"
            onClick={onPrint}
            disabled={isPDFGenerating || !initData?.form_fields}
          >
            <PrintIcon className="mr-2" />
            Print
          </button>
          <button className="btn btn-icon" onClick={closeModal}>
            <i className="icon-icon-x"></i>
          </button>
        </div>
      </h2>

      <div className="submit-on-behalf-modal__form">
        {formData && (
          <OnBehalfProvider submitter={onBehalfOf}>
            <RenderForm
              formData={formData}
              isSubmitter={true}
              isPrint={false}
              onSend={onSend}
              errors={errors}
              handelInitSettings={handelInitSettings}
              title={
                formData.title_options
                  ? {
                      ...formData.title_options,
                      options: { ...formData.title_options.options, value: formData.name },
                    }
                  : formData.name
              }
              formElement={initData?.form_fields}
              formHasOnlyOneSubmitter={formData.form_has_only_one_submitter || formData.is_one_submitter}
              initiatorID={formData.initiator_id}
              autoFillingVariables={formData.auto_filling_variables}
              submitterType={formData.submitter_type}
              submitterInfo={formData.submitter_info}
              strictOrderStep={formData.strict_order_step}
              submitter2Info={formData.submitter_2_info}
            />
          </OnBehalfProvider>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn btn-outline" onClick={onClearForm} disabled={!formData}>
          Clear form
        </button>
        <div className="submit-on-behalf-modal__actions">
          <button className="btn btn-outline" onClick={closeModal}>
            Cancel
          </button>
          <button className="btn" onClick={onSend} disabled={isDisabled || !formData}>
            Submit the form
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default SubmitOnBehalfModal;
